import React from 'react';
import { createPortal } from 'react-dom';
import { Modal } from '@carbon/react';
import type { AliasEdge } from 'types/api-responses';
import type { FlagSeverity } from 'types/flags';
import { EntityFlagTag } from 'components/entity-flag-tag';
import { entityIdSuffix } from 'utils/entity-id';
import styles from './alias-cluster-graph.module.scss';

// Ported from mock-ui entity-reconciliation v3 (AliasClusterGraph). HTML nodes
// (so names wrap without truncation) over an SVG edge layer. The cluster's
// Primary anchors the top; the other members spread along the bottom row. The
// *actual* alias edges (from the Alias table) are drawn as plain undirected
// lines between the connected nodes — so the real topology shows (a hub reads
// as a fan, a chain as a chain), not a star radiating from the viewed entity.
// Edges are undirected on purpose: the stored from/to direction isn't meaningful.
// Presentational — the caller fetches the cluster
// (GET /entities/:id/aliased_entities) and maps it to `members` + `edges`.
export interface AliasClusterMember {
  name: string;
  entityId: string;
  entityType: string;
  /** The cluster's canonical member — anchored at the top + labeled "Primary". */
  isPrimary?: boolean;
  /** Consolidation-risk flag category (null when unflagged) — badges the node. */
  flagCategory?: string | null;
  /** Free-text reason on the flag — shown on hover over the flag label. */
  flagRationale?: string | null;
  /**
   * Flag display severity. Only an active flag ('warning' workspace / 'info'
   * interpretation-only) badges the node; a dormant flag (null) is not badged.
   */
  flagSeverity?: FlagSeverity | null;
}

interface AliasClusterGraphProps {
  open: boolean;
  onClose: () => void;
  /** The entity whose pill was clicked — highlighted on the graph. */
  selectedEntityId?: string;
  members: AliasClusterMember[];
  /** Actual alias edges among the members (entity ids); drawn undirected. */
  edges: AliasEdge[];
}

// Stable class on the graph modal so a parent modal it opens from (the reconcile
// tearsheet, or the override modal) can list it in `selectorsFloatingMenus` and
// stop its focus trap from yanking focus back.
export const ALIAS_CLUSTER_GRAPH_CLASS = 'alias-cluster-graph-modal';
export const ALIAS_CLUSTER_GRAPH_SELECTOR = `.${ALIAS_CLUSTER_GRAPH_CLASS}`;

const H = 460;
const TOP_Y = 90;
const BOTTOM_Y = H - 90;

const AliasClusterGraph: React.FC<AliasClusterGraphProps> = ({
  open,
  onClose,
  selectedEntityId,
  members,
  edges,
}) => {
  // Anchor: the cluster's Primary (canonical); else the entity opened from; else
  // the first member — so the layout always has a stable top anchor.
  const topNode =
    members.find((m) => m.isPrimary) ??
    members.find((m) => m.entityId === selectedEntityId) ??
    members[0];
  const bottomNodes = members.filter((m) => m.entityId !== topNode?.entityId);

  const n = bottomNodes.length;
  // Node width shrinks a little as the row grows; the canvas WIDTH then grows to
  // fit them at a fixed center-to-center step, so members never overlap however
  // large the cluster is (the modal body scrolls horizontally past ~5 members).
  const nodeWidthRem = n >= 5 ? 8 : n >= 4 ? 10 : 12;
  const nodePx = nodeWidthRem * 16;
  const step = nodePx + 28; // center-to-center spacing (≥ node width → no overlap)
  const marginX = nodePx / 2 + 24; // keep the edge nodes off the canvas border
  const usableWidth = n <= 1 ? 0 : (n - 1) * step;
  const W = Math.max(720, Math.round(usableWidth + 2 * marginX));
  const topX = W / 2;

  const bottomPositions = bottomNodes.map((member, i) => ({
    member,
    x: n <= 1 ? topX : marginX + step * i,
    y: BOTTOM_Y,
  }));

  // entityId -> position, so we can lay the real edges over the nodes.
  const positionById = new Map<string, { x: number; y: number }>();
  if (topNode) positionById.set(topNode.entityId, { x: topX, y: TOP_Y });
  bottomPositions.forEach((p) =>
    positionById.set(p.member.entityId, { x: p.x, y: p.y })
  );

  // Each edge is a line between the two connected node centers. Lines tuck
  // behind the nodes (nodes sit above the SVG), so no arrowhead/clearance.
  const edgeLines = edges
    .map((edge, i) => {
      const s = positionById.get(String(edge.from));
      const t = positionById.get(String(edge.to));
      if (!s || !t) return null;
      return {
        key: `edge-${edge.from}-${edge.to}-${i}`,
        x1: s.x,
        y1: s.y,
        x2: t.x,
        y2: t.y,
      };
    })
    .filter((line): line is NonNullable<typeof line> => line !== null);

  const renderNode = (
    member: AliasClusterMember,
    x: number,
    y: number,
    isTop: boolean
  ) => {
    const isSelected = member.entityId === selectedEntityId;
    const className = [
      styles.node,
      isTop ? styles.nodeTop : '',
      isSelected ? styles.nodeSelected : '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <a
        key={`node-${member.entityId}`}
        href={member.entityId ? `/entities/${member.entityId}` : undefined}
        target="_blank"
        rel="noreferrer"
        className={className}
        style={{ left: `${x}px`, top: `${y}px`, width: `${nodeWidthRem}rem` }}
      >
        <div className={styles.nodeName}>
          {member.name}
          {member.entityId ? ` ${entityIdSuffix(member.entityId)}` : ''}
        </div>
        <div className={styles.nodeType}>
          {member.entityType}
          {member.isPrimary && (
            <span className={styles.primary}> · Primary</span>
          )}
        </div>
        {/* Same yellow/blue pill as the reconcile table; renders nothing only for
            an unflagged member (EntityFlagTag self-gates on severity). */}
        <EntityFlagTag
          className={styles.nodeFlagPill}
          flagCategory={member.flagCategory}
          flagRationale={member.flagRationale}
          flagSeverity={member.flagSeverity}
        />
      </a>
    );
  };

  const modal = (
    <Modal
      className={ALIAS_CLUSTER_GRAPH_CLASS}
      open={open}
      modalHeading="Alias cluster"
      passiveModal
      size="lg"
      onRequestClose={onClose}
    >
      <div className={styles.root}>
        <div
          className={styles.canvas}
          style={{ width: `${W}px`, height: `${H}px` }}
        >
          <svg
            viewBox={`0 0 ${W} ${H}`}
            width={W}
            height={H}
            className={styles.svg}
            aria-label="Alias cluster graph"
          >
            {edgeLines.map((line) => (
              <line
                key={line.key}
                x1={line.x1}
                y1={line.y1}
                x2={line.x2}
                y2={line.y2}
                className={styles.edge}
              />
            ))}
          </svg>
          {topNode && renderNode(topNode, topX, TOP_Y, true)}
          {bottomPositions.map((p) => renderNode(p.member, p.x, p.y, false))}
        </div>
        <p className={styles.note}>
          Lines connect records that are aliased together. Click a node to open
          its Entity Profile in a new tab.
        </p>
        {members.some((m) => m.flagSeverity) && (
          <p className={styles.legend}>
            A yellow flag gates consolidation (the entity is part of a workspace
            or interpretation); blue is informational. Hover a flag for its
            reason.
          </p>
        )}
      </div>
    </Modal>
  );

  // Portal to <body>: this graph opens from inside the reconcile tearsheet,
  // which is itself a Carbon Modal. Carbon's modal container is `transform`ed,
  // and a transformed ancestor becomes the containing block for a descendant
  // `position: fixed` — so a nested modal would center on the outer modal's box
  // (offset to its corner, width-constrained → the graph canvas clipped) rather
  // than the viewport. Rendering at the body escapes that containing block.
  return typeof document === 'undefined'
    ? modal
    : createPortal(modal, document.body);
};

export default AliasClusterGraph;
